import { Overlay, OverlayConfig, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';
import {
  ComponentRef,
  Directive,
  ElementRef,
  EventEmitter,
  HostListener,
  Input,
  OnDestroy,
  OnInit,
  Optional,
  Output,
  Self,
  ViewContainerRef,
} from '@angular/core';
import { NgControl } from '@angular/forms';
import {
  debounceTime,
  distinctUntilChanged,
  of,
  Subject,
  Subscription,
  switchMap,
} from 'rxjs';

import { EmailSuggestionService } from '../../../../services/email-suggestion.service';

import { EmailSuggestOverlayComponent } from './email-suggest-overlay.component';

@Directive({
  selector: '[appEmailSuggest]',
})
export class EmailSuggestDirective implements OnInit, OnDestroy {
  @Input() suggestionLimit = 8;
  @Input() minChars = 1;
  @Output() emailSelected = new EventEmitter<string>();

  private overlayRef: OverlayRef | null = null;
  private searchSubject$ = new Subject<string>();
  private subscriptions = new Subscription();
  private suggestions: string[] = [];
  private activeIndex = -1;
  private overlayComponentRef: ComponentRef<EmailSuggestOverlayComponent> | null =
    null;

  private isSelecting = false;
  private lastSelectedEmail: string | null = null;

  constructor(
    private elementRef: ElementRef<HTMLInputElement>,
    private overlay: Overlay,
    private viewContainerRef: ViewContainerRef,
    private emailSuggestionService: EmailSuggestionService,
    @Optional() @Self() private ngControl: NgControl
  ) {}

  ngOnInit(): void {
    const sub = this.searchSubject$
      .pipe(
        debounceTime(200),
        distinctUntilChanged(),
        switchMap((query) => {
          if (this.isSelecting) {
            return of([]);
          }
          const trimmed = (query || '').trim();
          if (
            trimmed.length < this.minChars ||
            trimmed === this.lastSelectedEmail
          ) {
            this.closeOverlay();
            return of([]);
          }
          return this.emailSuggestionService.getSuggestions(
            trimmed,
            this.suggestionLimit
          );
        })
      )
      .subscribe((results) => {
        if (this.isSelecting) {
          this.closeOverlay();
          return;
        }
        this.suggestions = results || [];
        this.activeIndex = -1;
        if (this.suggestions.length > 0) {
          this.showOverlay();
        } else {
          this.closeOverlay();
        }
      });

    this.subscriptions.add(sub);
  }

  @HostListener('input', ['$event'])
  onInput(event: Event): void {
    if (this.isSelecting) {
      return;
    }
    const value = (event.target as HTMLInputElement).value;
    if (value === this.lastSelectedEmail) {
      return;
    }
    this.lastSelectedEmail = null;
    this.searchSubject$.next(value);
  }

  @HostListener('focus')
  onFocus(): void {
    if (this.isSelecting) {
      return;
    }
    const value = this.elementRef.nativeElement.value;
    if (value && value === this.lastSelectedEmail) {
      return;
    }
    if (value && value.length >= this.minChars) {
      this.searchSubject$.next(value);
    }
  }

  @HostListener('blur')
  onBlur(): void {
    const value = this.elementRef.nativeElement.value;
    if (value && this.emailSuggestionService.isValidEmail(value)) {
      this.emailSuggestionService.saveEmail(value).subscribe();
    }

    // Delay closing so any clicked item in overlay completes its selection first
    setTimeout(() => {
      this.closeOverlay();
    }, 200);
  }

  @HostListener('keydown', ['$event'])
  onKeydown(event: KeyboardEvent): void {
    if (!this.overlayRef || !this.overlayRef.hasAttached()) {
      return;
    }

    switch (event.key) {
      case 'ArrowDown':
        event.preventDefault();
        this.activeIndex = Math.min(
          this.activeIndex + 1,
          this.suggestions.length - 1
        );
        this.updateOverlayInputs();
        break;

      case 'ArrowUp':
        event.preventDefault();
        this.activeIndex = Math.max(this.activeIndex - 1, 0);
        this.updateOverlayInputs();
        break;

      case 'Enter':
        if (this.activeIndex >= 0 && this.suggestions[this.activeIndex]) {
          event.preventDefault();
          this.selectSuggestion(this.suggestions[this.activeIndex]);
        }
        break;

      case 'Escape':
        event.preventDefault();
        this.closeOverlay();
        break;
    }
  }

  private getHostElement(): HTMLElement {
    return (
      (this.elementRef.nativeElement.closest('mat-form-field') as HTMLElement) ||
      this.elementRef.nativeElement
    );
  }

  private showOverlay(): void {
    const hostElement = this.getHostElement();
    const hostWidth = Math.max(
      hostElement.getBoundingClientRect().width,
      240
    );

    if (!this.overlayRef) {
      const positionStrategy = this.overlay
        .position()
        .flexibleConnectedTo(hostElement)
        .withPositions([
          {
            originX: 'start',
            originY: 'bottom',
            overlayX: 'start',
            overlayY: 'top',
          },
          {
            originX: 'start',
            originY: 'top',
            overlayX: 'start',
            overlayY: 'bottom',
          },
        ]);

      const overlayConfig = new OverlayConfig({
        positionStrategy,
        scrollStrategy: this.overlay.scrollStrategies.reposition(),
        width: hostWidth,
        minWidth: 200,
      });

      this.overlayRef = this.overlay.create(overlayConfig);
    } else {
      this.overlayRef.updateSize({ width: hostWidth });
    }

    if (!this.overlayRef.hasAttached()) {
      const portal = new ComponentPortal(
        EmailSuggestOverlayComponent,
        this.viewContainerRef
      );
      this.overlayComponentRef = this.overlayRef.attach(portal);

      this.overlayComponentRef.instance.selectItem.subscribe((email: string) => {
        this.selectSuggestion(email);
      });

      this.overlayComponentRef.instance.deleteItem.subscribe((email: string) => {
        this.removeSuggestion(email);
      });
    }

    this.updateOverlayInputs();
  }

  private removeSuggestion(email: string): void {
    this.emailSuggestionService.deleteEmail(email).subscribe();
    this.suggestions = this.suggestions.filter((item) => item !== email);
    if (this.suggestions.length > 0) {
      this.updateOverlayInputs();
    } else {
      this.closeOverlay();
    }
  }

  private updateOverlayInputs(): void {
    if (this.overlayComponentRef && this.overlayComponentRef.instance) {
      this.overlayComponentRef.instance.suggestions = [...this.suggestions];
      this.overlayComponentRef.instance.activeIndex = this.activeIndex;
      // Force change detection on dynamically attached portal component
      this.overlayComponentRef.changeDetectorRef.detectChanges();
    }
  }

  private selectSuggestion(email: string): void {
    this.isSelecting = true;
    this.lastSelectedEmail = email;
    this.closeOverlay();

    if (this.emailSelected.observers.length > 0) {
      this.emailSelected.emit(email);
      this.elementRef.nativeElement.value = '';

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue('');
      }
    } else {
      this.elementRef.nativeElement.value = email;

      if (this.ngControl && this.ngControl.control) {
        this.ngControl.control.setValue(email);
        this.ngControl.control.markAsDirty();
        this.ngControl.control.markAsTouched();
      }

      // Trigger DOM events for native event listeners & Angular validators
      this.elementRef.nativeElement.dispatchEvent(
        new Event('input', { bubbles: true })
      );
      this.elementRef.nativeElement.dispatchEvent(
        new Event('change', { bubbles: true })
      );
    }

    // Save/update usage in backend
    this.emailSuggestionService.saveEmail(email).subscribe();

    // Reset selection flag after the synthetic events and debounce interval complete
    setTimeout(() => {
      this.isSelecting = false;
    }, 350);
  }

  private closeOverlay(): void {
    if (this.overlayRef && this.overlayRef.hasAttached()) {
      this.overlayRef.detach();
    }
    this.activeIndex = -1;
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
    if (this.overlayRef) {
      this.overlayRef.dispose();
      this.overlayRef = null;
    }
  }
}
